<template lang="pug">
div
  // Header row para desktop
  div.container.d-none.d-md-block
    b-row
      b-col(cols='6').d-flex.justify-content-flex-start.align-items-center
        b-icon.back-arrow.h3.cursor-pointer(
          icon="arrow-left-square-fill"
          variant=""
          @click="$router.go(-1)"
        )
        h4.pl-3.title Excepciones OV
      b-col(cols='6').d-flex.justify-content-end.align-items-center.mb-2
        div.position-relative.search-container
          b-icon(icon="search" class="search-icon")
          b-form-input(
            v-model="searchTerm"
            placeholder="Buscar por OV / RMA / N° de Serie"
            @keydown.enter="handleSearch"
            class="search-input"
          )

    div
      b-tabs(pills card lazy v-model="tabSelected" @activate-tab="onTabChange")
        b-tab(
          v-for="(tab, index) in tabs"
          :key="index"
          :title="`${tab.label} (${tab.count})`"
        )

      b-row
        b-col
          b-card(no-body)
            div.text-center.py-5(v-if="loading")
              b-spinner(variant="warning" label="Cargando...")
            b-table(
              v-else
              :items="exceptions"
              :fields="fields"
              responsive
              striped
              hover
              show-empty
              empty-text="No hay excepciones"
              class="mb-0"
            )
              template(#cell(fecha_creacion)="data")
                | {{ formatDate(data.value) }}
              
              template(#cell(tipo)="data")
                | {{ data.value }}
              
              template(#cell(solicitado_por)="data")
                | {{ data.value || '-' }}

              template(#cell(rma)="data")
                | {{ data.value || '-' }}

              template(#cell(numero_serie_equipo)="data")
                | {{ data.value || '-' }}

              template(#cell(acciones)="data")
                b-button(
                  size="sm"
                  variant="warning"
                  @click="openExceptionModal(data.item)"
                ) Editar
          
          div.d-flex.justify-content-end.my-3(v-if="totalRows > 0")
            b-pagination(
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="20"
              align="right"
              prev-text="Ant."
              next-text="Sig."
              aria-controls="my-table"
              @change="handlePageChange"
            )

    b-modal(
      id="confirmation-modal"
      :title="modalTitle"
      centered
      :ok-title="modalOkTitle"
      cancel-title="Cancelar"
      ok-variant="warning"
      cancel-variant="light"
      @ok="confirmAction"
      title-class="font-weight-bold"
    )
      p.mb-2 {{ modalDescription }}
      div(v-if="itemToProcess && itemToProcess.comentario")
        p.font-weight-bold.mb-1 Comentario:
        p.text-muted.mb-3 {{ itemToProcess.comentario }}
      p.font-weight-bold.mb-3 {{ modalMessage }}

    //- Modal de detalle de excepción
    ExceptionDetailModal(
      v-if="showExceptionModal"
      :exception-id="selectedExceptionId"
      :visible="showExceptionModal"
      @close="closeExceptionModal"
      @action-completed="handleExceptionAction"
    )

</template>

<script>
import { mapActions } from 'vuex'
import moment from 'moment'
import { wsClient } from '../../utils/websocket'
import ExceptionDetailModal from './ExceptionDetailModal.vue'

export default {
  name: 'ListExceptionsOV',
  components: {
    ExceptionDetailModal,
  },
  data() {
    return {
      searchTerm: '',
      exceptions: [],
      totalRows: 0,
      currentPage: 1,
      tabSelected: 0,
      tabs: [
        {
          label: 'Pendientes',
          key: 'pendientes',
          value: 'PENDIENTE',
          count: 0,
        },
        { label: 'Aprobadas', key: 'aprobadas', value: 'APROBADO', count: 0 },
        {
          label: 'Rechazadas',
          key: 'rechazadas',
          value: 'RECHAZADO',
          count: 0,
        },
        { label: 'Todas', key: 'total', value: 'TODAS', count: 0 },
      ],
      fields: [
        { key: 'ov_id', label: 'O.V.', sortable: true, tdClass: 'id-column' },
        { key: 'fecha_creacion', label: 'Fecha', sortable: true },
        { key: 'tipo', label: 'Tipo' },
        { key: 'estado', label: 'Estado', sortable: true },
        { key: 'solicitado_por', label: 'Solicitado por' },
        { key: 'rma', label: 'RMA' },
        { key: 'numero_serie_equipo', label: 'N° de Serie' },
        {
          key: 'acciones',
          label: 'Acciones',
          class: 'text-center',
          tdClass: 'actions-column',
        },
      ],
      itemToProcess: null,
      actionType: null,
      loading: false,
      // Modal de detalle
      showExceptionModal: false,
      selectedExceptionId: null,
    }
  },
  async created() {
    await this.fetchCounts()
    // Busqueda inicial por estado pendiente
    this.fetchExceptions(this.tabs[0].value, 1)

    // Escuchar actualizaciones de excepciones vía websocket
    this.unsubscribeExceptions = wsClient.on('excepcion:updated', () => {
      this.fetchCounts()
      this.fetchExceptions(this.tabs[this.tabSelected].value, this.currentPage)
    })
  },
  computed: {
    modalTitle() {
      return this.actionType === 'approve'
        ? 'Aprobación de Excepción'
        : 'Rechazo de Excepción'
    },
    modalMessage() {
      return this.actionType === 'approve'
        ? '¿Quieres aprobar esta excepción?'
        : '¿Quieres rechazar esta excepción?'
    },
    modalOkTitle() {
      return this.actionType === 'approve' ? 'Aprobar' : 'Rechazar'
    },
    modalDescription() {
      if (!this.itemToProcess) return ''
      const action = this.actionType === 'approve' ? 'aprobando' : 'rechazando'
      return `Estás ${action} una Excepción para la OV ${
        this.itemToProcess.ov_id
      }, por ${this.itemToProcess.tipo || 'ajuste de bono'}`
    },
  },
  methods: {
    ...mapActions('OVStore', [
      'listExceptions',
      'manageException',
      'getExceptionCounts',
    ]),

    async fetchCounts() {
      try {
        const counts = await this.getExceptionCounts()
        // Update counts in tabs
        this.tabs.forEach((tab) => {
          if (counts[tab.key] !== undefined) {
            tab.count = counts[tab.key]
          }
        })
      } catch (error) {
        console.error('Error fetching counts:', error)
      }
    },

    async fetchExceptions(estado, page = 1) {
      this.loading = true
      try {
        const response = await this.listExceptions({
          estado,
          page,
          search: this.searchTerm,
        })
        this.exceptions = response.results
        this.totalRows = response.count
      } catch (error) {
        console.error('Error fetching exceptions:', error)
        this.$bvToast.toast('Error al cargar las excepciones', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.loading = false
      }
    },

    handleSearch() {
      this.currentPage = 1
      this.fetchExceptions(this.tabs[this.tabSelected].value, 1)
    },

    onTabChange(index) {
      this.currentPage = 1
      const selectedTab = this.tabs[index]
      this.fetchExceptions(selectedTab.value, 1)
    },

    formatDate(date) {
      if (!date) return '-'
      return moment(date).format('DD/MM/YYYY HH:mm')
    },

    approveException(item) {
      this.itemToProcess = item
      this.actionType = 'approve'
      this.$bvModal.show('confirmation-modal')
    },

    rejectException(item) {
      this.itemToProcess = item
      this.actionType = 'reject'
      this.$bvModal.show('confirmation-modal')
    },

    async confirmAction() {
      if (!this.itemToProcess) return

      const isApprove = this.actionType === 'approve'
      const estado = isApprove ? 'approved' : 'reject'
      const successMsg = isApprove
        ? 'Excepción aprobada correctamente'
        : 'Excepción rechazada correctamente'
      const errorMsg = isApprove
        ? 'Error al aprobar la excepción'
        : 'Error al rechazar la excepción'

      try {
        await this.manageException({
          ov_id: this.itemToProcess.ov_id,
          estado,
        })
        this.$bvToast.toast(successMsg, {
          title: 'Éxito',
          variant: 'success',
          solid: true,
        })
        await this.fetchCounts()
        this.fetchExceptions(
          this.tabs[this.tabSelected].value,
          this.currentPage
        )
      } catch (error) {
        this.$bvToast.toast(errorMsg, {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.itemToProcess = null
        this.actionType = null
      }
    },

    // Métodos para modal de detalle
    openExceptionModal(item) {
      this.selectedExceptionId = item.id
      this.showExceptionModal = true
    },

    closeExceptionModal() {
      this.showExceptionModal = false
      this.selectedExceptionId = null
    },

    async handleExceptionAction() {
      // Refrescar datos después de aprobar/rechazar
      await this.fetchCounts()
      this.fetchExceptions(this.tabs[this.tabSelected].value, this.currentPage)
      this.closeExceptionModal()
    },
  },
  beforeDestroy() {
    if (this.unsubscribeExceptions) {
      this.unsubscribeExceptions()
    }
  },
}
</script>

<style lang="scss" scoped>
@import '@/style/colors.scss';

::v-deep .nav-pills .nav-link.active,
.nav-pills .show > .nav-link {
  background-color: #f4c235;
  color: black;
  font-weight: bold;
  border-radius: 0.5rem;
}

::v-deep .back-arrow {
  color: $samtek-yellow;
  cursor: pointer;
}

::v-deep .nav-item a {
  color: #525252;
  font-weight: bold;
}

::v-deep .card-header-pills {
  background-color: #f5f6fa;
  border-radius: 0.5rem;
}
::v-deep .nav-item {
  margin: 6px;
}
::v-deep .card-header {
  background-color: #fff;
  border-bottom: none;
}

::v-deep .tab-content > .active {
  display: none;
}
::v-deep .b-table thead th {
  background-color: #dddde1;
  color: #525252;
  border: none;
}

::v-deep .b-table tbody td {
  border: none;
  background-color: #fff;
  vertical-align: middle;
}
::v-deep .b-table tbody tr td:first-child {
  font-weight: bold;
  color: black;
}

::v-deep .id-column {
  width: 60px;
}
::v-deep .actions-column {
  width: 200px;
}

::v-deep .dropdown-toggle {
  background-color: #f4c235 !important;
  color: black !important;
  border: none;
  font-weight: bold;

  &:hover,
  &:focus,
  &:active {
    background-color: #e0b020 !important;
    color: black !important;
    box-shadow: none !important;
  }
}

::v-deep .dropdown-menu {
  border: 1px solid #eee;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

::v-deep .dropdown-item:hover {
  background-color: #f4c235;
}

::v-deep .page-item.active .page-link {
  background-color: #f4c235;
  color: black;
}
::v-deep .page-link {
  color: black;
  border-color: black;
  border: 1px solid black;
  &:hover {
    color: black;
    border: 1px solid black;
    box-shadow: none;
  }
}

::v-deep tr {
  min-height: 70px;
  height: 70px;
}

.search-container {
  width: 350px;
}

.search-icon {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  color: #6c757d;
}

.search-input {
  padding-left: 40px;
  border-radius: 5px;
  border: 1px solid #ced4da;
}
</style>
